import { Format, FORMAT_MIME_TYPES } from './formats';

/**
 * Type for data values that can be stored in table cells
 */
export type DataValue = string | number | boolean | Date | null | undefined;

/**
 * Record type for a row of data
 */
export type DataRecord = Record<string, DataValue | object>;

/**
 * Options for exporting data
 */
export interface ExportOptions {
  /**
   * The format to export to
   */
  format: Format;
  /**
   * The filename to use (without extension)
   */
  filename: string;
  /**
   * Optional columns to include (if not provided, all columns will be included)
   */
  columns?: string[];
  /**
   * Optional callback to transform data before export
   */
  transform?: (data: DataRecord[]) => DataRecord[];
}

/**
 * Server-side export options
 */
export interface ServerExportOptions extends Omit<ExportOptions, 'transform'> {
  /**
   * The URL to send the export request to
   */
  apiUrl: string;
}

/**
 * Response from the getUploadUrls API
 */
interface UploadUrlsResponse {
  source: {
    uploadUrl: string;
    filePath: string;
    contentType: string;
  };
  destination: {
    uploadUrl: string;
    filePath: string;
    contentType: string;
  };
}

/**
 * Response from the convert API
 */
interface ConvertResponse {
  url: string;
  filePath: string;
}

/**
 * Triggers a download from a URL
 * @param url The URL to download from
 * @param filename The filename to use
 */
export function downloadFromUrl(url: string, filename: string): void {
  // Create a link element
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // Append to the document, click it, and remove it
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Gets signed URLs for uploading source and destination files
 * @param sourceFormat Source file format
 * @param destinationFormat Destination file format
 * @returns Promise with upload URLs for source and destination
 */
async function getUploadUrls(sourceFormat: Format, destinationFormat: Format): Promise<UploadUrlsResponse> {
  const response = await fetch('/api/convert/urls', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceFormat,
      destinationFormat
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to get upload URLs: ${error}`);
  }

  return response.json();
}

/**
 * Uploads data to a signed URL
 * @param data The data to upload
 * @param uploadUrl The signed URL to upload to
 * @param contentType The content type of the data
 */
async function uploadToSignedUrl(data: string | Blob, uploadUrl: string, contentType: string): Promise<void> {
  const blob = typeof data === 'string' 
    ? new Blob([data], { type: contentType }) 
    : data;

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload data: ${response.status} ${response.statusText}`);
  }
}

/**
 * Triggers the conversion process
 * @param sourcePath Path to the source file
 * @param destinationPath Path to the destination file
 * @param sourceFormat Source file format
 * @param destinationFormat Destination file format
 * @returns Promise with the URL to download the converted file
 */
async function triggerConversion(
  sourcePath: string,
  destinationPath: string,
  sourceFormat: Format,
  destinationFormat: Format
): Promise<ConvertResponse> {
  const response = await fetch('/api/convert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceUrl: sourcePath,
      sourceFormat,
      destinationFormat,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Conversion failed: ${error}`);
  }

  return response.json();
}

/**
 * Exports data to a file in the specified format using server-side conversion
 * @param data The data to export
 * @param options Export options including the API URL
 */
export async function exportDataViaServer(
  data: DataRecord[], 
  options: ServerExportOptions
): Promise<void> {
  const { format, filename, columns, apiUrl } = options;
  
  // Filter columns if specified
  const filteredData = columns 
    ? data.map(row => {
        const filteredRow: Record<string, DataValue | object> = {};
        columns.forEach(col => {
          if (row[col] !== undefined) {
            filteredRow[col] = row[col];
          }
        });
        return filteredRow;
      })
    : data;

  try {
    // Send the data to the server for conversion
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: filteredData,
        format,
        filename
      }),
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}: ${response.statusText}`);
    }

    // Get the URL to the converted file
    const result = await response.json();
    
    if (!result.url) {
      throw new Error('Server did not return a file URL');
    }

    // Trigger the download
    downloadFromUrl(result.url, `${filename}.${format}`);
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
}

/**
 * Creates a direct export URL for a file
 * @param fileId The ID of the file to export
 * @param format The format to export to
 * @param apiBaseUrl The base URL of the API
 * @returns The export URL
 */
export function createDirectExportUrl(
  fileId: string,
  format: Format,
  apiBaseUrl: string
): string {
  return `${apiBaseUrl}/export/${fileId}?format=${format}`;
}

/**
 * Downloads a file in the specified format
 * @param fileId The ID of the file to download
 * @param format The format to download in
 * @param filename The filename to use
 * @param apiBaseUrl The base URL of the API
 */
export function downloadFileInFormat(
  fileId: string,
  format: Format,
  filename: string,
  apiBaseUrl: string
): void {
  const url = createDirectExportUrl(fileId, format, apiBaseUrl);
  downloadFromUrl(url, `${filename}.${format}`);
}

/**
 * Exports data to a file in the specified format
 * @param data The data to export
 * @param options Export options
 */
export async function exportData(data: DataRecord[], options: ExportOptions): Promise<{ url: string }> {
  const { format, filename, columns, transform } = options;
  
  // Filter columns if specified
  const filteredData = columns 
    ? data.map(row => {
        const filteredRow: Record<string, DataValue | object> = {};
        columns.forEach(col => {
          if (row[col] !== undefined) {
            filteredRow[col] = row[col];
          }
        });
        return filteredRow;
      })
    : data;
  
  // Apply transform if specified
  const transformedData = transform ? transform(filteredData) : filteredData;
  
  // Convert data based on format
  let content: string | Blob;
  let contentType: string;
  
  switch (format) {
    case Format.CSV:
      content = convertToDelimitedText(transformedData, ',');
      contentType = FORMAT_MIME_TYPES[Format.CSV];
      break;
      
    case Format.TSV:
      content = convertToDelimitedText(transformedData, '\t');
      contentType = FORMAT_MIME_TYPES[Format.TSV];
      break;
      
    case Format.JSON:
      content = JSON.stringify(transformedData, null, 2);
      contentType = FORMAT_MIME_TYPES[Format.JSON];
      break;
      
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
  
  // Create a blob URL for the content
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  
  return { url };
}

/**
 * Converts data to a delimited text format (CSV/TSV)
 * @param data The data to convert
 * @param delimiter The delimiter to use
 * @returns The delimited text
 */
function convertToDelimitedText(data: DataRecord[], delimiter: string): string {
  if (!data || data.length === 0) return '';
  
  // Get headers from first row
  const headers = Object.keys(data[0]);
  
  // Convert headers and rows to delimited format
  const headerRow = headers.map(h => formatDelimitedValue(h, delimiter)).join(delimiter);
  const rows = data.map(row => 
    headers.map(header => formatDelimitedValue(row[header], delimiter)).join(delimiter)
  );
  
  return [headerRow, ...rows].join('\n');
}

/**
 * Formats a value for delimited text (CSV/TSV)
 * @param value The value to format
 * @param delimiter The delimiter being used
 * @returns The formatted value
 */
function formatDelimitedValue(value: DataValue | object, delimiter: string): string {
  if (value == null) return '';
  
  const stringValue = String(value);
  
  // Check if we need to quote the value
  const needsQuoting = 
    stringValue.includes(delimiter) || 
    stringValue.includes('"') || 
    stringValue.includes('\n') ||
    stringValue.includes('\r');
    
  if (!needsQuoting) return stringValue;
  
  // Escape quotes and wrap in quotes
  return `"${stringValue.replace(/"/g, '""')}"`;
} 